import { useEffect, useMemo, useState } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { getAllPermissionConfig, createPermission, updatePermission, updatePermissionOrder, deletePermission } from "../../redux/features/user/userAsyncThunk";
import { selectUsers } from "../../redux/features/user/userSlice";
import { PermissionConfigFor } from "../../redux/features/auth/IAuthState";
import PermissionGroup from "./PermissionGroup";
import { UserPermissionConfig } from "../../redux/features/user/types";
import { PageTitle, StyledLists, Container, StyledButton } from "./styles";
import StyledModal from "../../components/CustomModal";
import { Form, Input, Select } from "antd";
import { FooterButtons } from "../Category/styles";
import { RequestResults } from "../../redux/common/RequestStatus";

const PermissionKey = () => {
  const dispatch = useAppDispatch();
  const { allUserPermissions, status } = useAppSelector(selectUsers);

  const [permissions, setPermissions] = useState<UserPermissionConfig[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<UserPermissionConfig | null>(null);
  const [form] = Form.useForm();
  const [deleteForm] = Form.useForm();
  const selectedDeleteReason = Form.useWatch('delete', deleteForm);
  const [loading, setLoading] = useState(false);

  // Delete confirmation modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    dispatch(getAllPermissionConfig());
  }, [dispatch]);

  useEffect(() => {
    if (allUserPermissions) {
      setPermissions(allUserPermissions);
    }
  }, [allUserPermissions]);

  const handleEditClick = (permission: UserPermissionConfig) => {
    setEditingPermission(permission);
    form.setFieldsValue({
      configName: permission.configName,
      configKey: permission.configKey,
      configType: permission.configType,
    });
    setIsModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setEditingPermission(null);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      if (editingPermission) {
        await dispatch(
          updatePermission({
            id: editingPermission.id,
            data: {
              configName: values.configName,
              configKey: values.configKey,
              configType: values.configType,
              index: editingPermission.index
            },
          })
        );
      } else {
        await dispatch(
          createPermission({
            data: {
              configName: values.configName,
              configKey: values.configKey,
              configType: values.configType,
              index: 0,
              allowed: true,
            },
          })
        );
      }
      setIsModalOpen(false);
      setEditingPermission(null);
      form.resetFields();
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const configurationGroup = useMemo(() => {
    const groups = [
      {
        key: PermissionConfigFor.MenuVisibility.toString(),
        label: "Menu Visibility",
        configType: PermissionConfigFor.MenuVisibility.toString(),
        configuration: permissions?.filter((permission) => permission?.configType === PermissionConfigFor.MenuVisibility).sort((a, b) => a?.index - b?.index) || [],
      },
      {
        key: PermissionConfigFor.SystemManagement.toString(),
        label: "System Management",
        configType: PermissionConfigFor.SystemManagement.toString(),
        configuration: permissions?.filter((permission) => permission?.configType === PermissionConfigFor.SystemManagement).sort((a, b) => a?.index - b?.index) || [],
      },
      {
        key: PermissionConfigFor.PaymentManagement.toString(),
        label: "Payment Management",
        configType: PermissionConfigFor.PaymentManagement.toString(),
        configuration: permissions?.filter((permission) => permission?.configType === PermissionConfigFor.PaymentManagement).sort((a, b) => a?.index - b?.index) || [],
      },
      {
        key: PermissionConfigFor.StatusPermissions.toString(),
        label: "Status Permissions",
        configType: PermissionConfigFor.StatusPermissions.toString(),
        configuration: permissions?.filter((permission) => permission?.configType === PermissionConfigFor.StatusPermissions).sort((a, b) => a?.index - b?.index) || [],
      },
    ];
    return groups;
  }, [permissions]);


  const handleDragEnd = (result: any) => {
    try {
      if (!result.destination) return;

      const { source, destination } = result;
      const sourceGroup = configurationGroup.find((group) => group.key === source.droppableId);
      const destGroup = configurationGroup.find((group) => group.key === destination.droppableId);

      if (!sourceGroup || !destGroup) return;

      const updatedPermissions = [...permissions];
      //groped permissions by config type 
      const groupedPermissions = updatedPermissions.reduce((acc: Record<string, UserPermissionConfig[]>, permission) => {
        const group = acc[permission.configType] || [];
        group.push(permission);
        acc[permission.configType] = group.sort((a, b) => a?.index - b?.index);
        return acc;
      }, {} as Record<string, UserPermissionConfig[]>);

      const sourceGroupPermissions = groupedPermissions[sourceGroup.configType];
      const destGroupPermissions = groupedPermissions[destGroup.configType];

      if (sourceGroup.configType !== destGroup.configType) {
        const movedItem = { ...sourceGroupPermissions.splice(source.index, 1)[0] };
        movedItem.configType = parseInt(destGroup.configType);
        destGroupPermissions.splice(destination.index, 0, movedItem);
      } else {
        const movedItem = sourceGroupPermissions.splice(source.index, 1)[0];
        destGroupPermissions.splice(destination.index, 0, movedItem);
      }

      const reIndexItems = (items: UserPermissionConfig[]) => items.map((item, idx) => ({ ...item, index: idx }));

      if (sourceGroup.configType !== destGroup.configType) {
        groupedPermissions[sourceGroup.configType] = reIndexItems(sourceGroupPermissions);
        groupedPermissions[destGroup.configType] = reIndexItems(destGroupPermissions);
      } else {
        groupedPermissions[sourceGroup.configType] = reIndexItems(sourceGroupPermissions);
      }

      setPermissions(Object.values(groupedPermissions).flat());

      const updatedGroupPermissions = sourceGroup.configType !== destGroup.configType ? [...reIndexItems(sourceGroupPermissions), ...reIndexItems(destGroupPermissions)] : [...reIndexItems(sourceGroupPermissions)];
      dispatch(updatePermissionOrder({ orderUpdates: updatedGroupPermissions }));
    } catch (error) {
      console.error("Error updating permissions order:", error);
    }
  };

  const showDeleteConfirmation = (id: string) => {
    setPermissionToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setPermissionToDelete(null);
  };

  const handleDeletePermission = async () => {
    if (permissionToDelete && selectedDeleteReason?.toLowerCase() === 'delete') {
      try {
        setDeleteLoading(true);
        await dispatch(deletePermission({ id: permissionToDelete }));
        setIsDeleteModalOpen(false);
        setPermissionToDelete(null);
      } catch (error) {
        console.error('Error deleting permission:', error);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  return (
    <>
      <PageTitle>Permission Management</PageTitle>
      <Container>
        <StyledLists>
          <DragDropContext onDragEnd={handleDragEnd}>
            {configurationGroup.map((group) => (
              <PermissionGroup
                key={group.key}
                loading={status === RequestResults.pending}
                group={group}
                onEditPermission={handleEditClick}
                onDeletePermission={showDeleteConfirmation}
                onAddPermission={(configType) => {
                  form.setFieldsValue({ configType });
                  setIsModalOpen(true);
                }}
              />
            ))}
          </DragDropContext>
        </StyledLists>
      </Container>

      <StyledModal
        draggable={true}
        title={editingPermission ? "Edit Permission" : "Add New Permission"}
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={[
          <FooterButtons>
            <StyledButton key="cancel" onClick={handleModalCancel} disabled={loading}>
              Cancel
            </StyledButton>
            <StyledButton type="primary" key="submit" onClick={handleModalOk} loading={loading}>
              {editingPermission ? "Update" : "Create"}
            </StyledButton>
          </FooterButtons>,
        ]}
      >
        <Form form={form} style={{ marginTop: 10 }} layout="vertical" disabled={loading}>
          <Form.Item name="configName" label="Permission Name" rules={[{ required: true, message: "Please enter permission name" }]}>
            <Input size="large" />
          </Form.Item>
          <Form.Item name="configKey" normalize={(value) => value?.trim() || ""} label="Config Key" rules={[{ required: true, message: "Please enter config key" }]}>
            <Input size="large" />
          </Form.Item>
          <Form.Item name="configType" label="Config Type" rules={[{ required: true, message: "Please select config type" }]}>
            <Select size="large">
              <Select.Option value={PermissionConfigFor.MenuVisibility}>Menu Visibility</Select.Option>
              <Select.Option value={PermissionConfigFor.SystemManagement}>System Management</Select.Option>
              <Select.Option value={PermissionConfigFor.PaymentManagement}>Payment Management</Select.Option>
              <Select.Option value={PermissionConfigFor.StatusPermissions}>Status Permissions</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </StyledModal>

      {/* Delete Confirmation Modal */}
      <StyledModal
        draggable={true}
        title="Are you sure you want delete?"
        open={isDeleteModalOpen}
        onCancel={handleDeleteCancel}
        footer={[
          <FooterButtons key="delete-footer">
            <StyledButton key="cancel" onClick={handleDeleteCancel} disabled={deleteLoading}>
              Cancel
            </StyledButton>
            <StyledButton
              type="primary"
              danger
              key="delete"
              onClick={handleDeletePermission}
              loading={deleteLoading}
              disabled={selectedDeleteReason?.toLowerCase() !== 'delete'}
            >
              Delete
            </StyledButton>
          </FooterButtons>,
        ]}
      >
        <Form layout="vertical" form={deleteForm} style={{ marginTop: 10 }}>
          <Form.Item
            name="delete"
            label="Write 'delete' to confirm"
            rules={[
              { required: true, message: "" },
              {
                validator(_, value) {
                  if (value && value.trim().toLowerCase() === "delete") {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Please enter 'delete' exactly"));
                },
              },
            ]}
          >
            <Input type="text" placeholder="Write 'delete' to confirm" name="delete" />
          </Form.Item>
        </Form>
      </StyledModal>
    </>
  );
};

export default PermissionKey;
